import {
  HarnessTurnContext,
  HarnessWriteTarget,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import { HarnessInterruptType } from '@flowx/designer/modules/ai-builder/types/harness.types'

/**
 * What one call on a conversation-owned harness carries (MT-3910, doc 37 N1): the project its
 * headers name, the version and branch its body repeats, and where the maker is.
 */
export type HarnessCallScope = {
  workspaceId: string
  appId: string
  /** `app_version_id` and `branch`, as every execution, abandon and pause body repeats them. */
  body: { app_version_id: string; branch: string }
  /** The turn's `context{…}`: where the maker is, which never moves the target. */
  context: HarnessTurnContext
}

/** The gates whose answer acts on the plan, which makes their `/resume` a plan call (doc 37 N1).
 * Answering any other — `ask_user`, `read_grant` — is a turn. */
const PLAN_GATES: ReadonlySet<HarnessInterruptType> = new Set<HarnessInterruptType>([
  'brief_review',
  'plan_review',
  'direct_review',
  'specialist_review',
])

export function isPlanGate(type: HarnessInterruptType | null | undefined): boolean {
  return !!type && PLAN_GATES.has(type)
}

/**
 * A turn's scope: the page the maker is on, for its headers, its body and its context alike
 * (doc 37 N1). The relay authorizes the page, and the harness checks the maker's own access to
 * the write target when the turn starts, so a turn never carries the target's headers.
 *
 * Off a project page — organization settings — it falls back to the target's head, then to where
 * the maker last spoke: the harness needs a version to read at, and those are the two it already
 * knows.
 */
export function turnScopeFor(
  target: HarnessWriteTarget | null,
  route: HarnessTurnContext | null,
  lastContext: HarnessTurnContext | null
): HarnessCallScope | null {
  const where = pageOf(target, route, lastContext)

  return (
    where && {
      workspaceId: where.workspace_id,
      appId: where.app_id,
      body: { app_version_id: where.app_version_id, branch: where.branch },
      context: where,
    }
  )
}

/**
 * A plan call's scope: answering a review of the plan, running or previewing it, amending it,
 * abandoning it (doc 37 N1).
 *
 * **Headers** are the write target's once there is one: the relay's `AIAGENT_EDIT` on them is the
 * permission to change it, and the harness refuses any other (`409 target_headers_required`).
 * Before a target they are the page's, as on a turn — the first change lands where the maker is.
 *
 * **The body** names the target's head once there is one: the harness acts on the target whatever
 * is sent, and a body that agrees with it cannot be misread.
 *
 * **The context** is the page, as on a turn.
 */
export function planScopeFor(
  target: HarnessWriteTarget | null,
  route: HarnessTurnContext | null,
  lastContext: HarnessTurnContext | null
): HarnessCallScope | null {
  const where = pageOf(target, route, lastContext)

  if (!where) {
    return null
  }

  const project = target ?? where

  return {
    workspaceId: project.workspace_id,
    appId: project.app_id,
    body: target
      ? { app_version_id: target.head_version_id, branch: target.branch_name || 'main' }
      : { app_version_id: where.app_version_id, branch: where.branch },
    context: where,
  }
}

/** The target's head, read as a context: the version the conversation's own work is at. */
export function targetContext(target: HarnessWriteTarget): HarnessTurnContext {
  return {
    workspace_id: target.workspace_id,
    app_id: target.app_id,
    app_version_id: target.head_version_id,
    branch: target.branch_name || 'main',
  }
}

/** Where the maker is: the page, else the target's head, else where they last spoke. */
function pageOf(
  target: HarnessWriteTarget | null,
  route: HarnessTurnContext | null,
  lastContext: HarnessTurnContext | null
): HarnessTurnContext | null {
  return route ?? (target ? targetContext(target) : lastContext)
}
